import React, { useState, useEffect } from 'react'
import api from '../../services/api'
import { showSuccess, showError, showWarning } from '../../utils/alert'

function ExitValidations() {
  const [validations, setValidations] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [statusFilter, setStatusFilter] = useState('Pending')
  const [selectedValidation, setSelectedValidation] = useState(null)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [withdrawalData, setWithdrawalData] = useState(null)
  const [password, setPassword] = useState('')
  const [catatan, setCatatan] = useState('')
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    fetchValidations()
  }, [page, statusFilter])

  const fetchValidations = async () => {
    try {
      setLoading(true)
      const response = await api.get('/members/validasi-keluar', {
        params: { page, limit: 20, status: statusFilter }
      })
      setValidations(response.data.data || [])
      const meta = response.data.meta
      setTotalPages(meta?.totalPages || 1)
    } catch (error) {
      console.error('Error fetching validations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    if (!password) {
      showWarning('Password harus diisi')
      return
    }

    setProcessing(true)
    try {
      await api.post(`/members/validasi-keluar/${selectedValidation.Id}/approve`, {
        password,
        catatan
      })
      showSuccess('Validasi berhasil disetujui')
      setShowApproveModal(false)
      setSelectedValidation(null)
      setPassword('')
      setCatatan('')
      fetchValidations()
    } catch (error) {
      showError('Gagal menyetujui validasi', error.response?.data?.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleWithdrawal = async () => {
    setProcessing(true)
    try {
      const response = await api.post(`/members/validasi-keluar/${selectedValidation.Id}/withdraw`)
      setWithdrawalData(response.data.data)
      setShowWithdrawModal(true)
      fetchValidations()
    } catch (error) {
      showError('Gagal memproses penarikan', error.response?.data?.message)
    } finally {
      setProcessing(false)
    }
  }

  const handlePrintReceipt = () => {
    if (!withdrawalData) return

    const receiptContent = `
================================
   BUKTI PENARIKAN SIMPANAN
      ANGGOTA KELUAR
================================
NBA       : ${withdrawalData.nba}
Nama      : ${withdrawalData.nama}
Tanggal   : ${new Date().toLocaleDateString('id-ID')}
--------------------------------
Simpanan Pokok   : Rp ${withdrawalData.simpananPokok.toLocaleString('id-ID')}
Potongan Penalty : Rp ${withdrawalData.potonganPokok.toLocaleString('id-ID')}
Ditarik          : Rp ${withdrawalData.tarikPokok.toLocaleString('id-ID')}
--------------------------------
Simpanan Wajib   : Rp ${withdrawalData.simpananWajib.toLocaleString('id-ID')}
Potongan Penalty : Rp ${withdrawalData.potonganWajib.toLocaleString('id-ID')}
Ditarik          : Rp ${withdrawalData.tarikWajib.toLocaleString('id-ID')}
================================
Total Penarikan  : Rp ${withdrawalData.totalPenarikan.toLocaleString('id-ID')}
Penalty          : ${withdrawalData.penaltyPercent}%
================================
      Tanda Tangan
    (Koperasi SP)
================================
`

    // Send to PLQ20 printer
    const printWindow = window.open('', '_blank')
    printWindow.document.write('<pre>' + receiptContent + '</pre>')
    printWindow.document.close()
    printWindow.print()
  }

  const handleReject = async () => {
    if (!password) {
      showWarning('Password harus diisi')
      return
    }

    setProcessing(true)
    try {
      await api.post(`/members/validasi-keluar/${selectedValidation.Id}/reject`, {
        password,
        catatan
      })
      showSuccess('Validasi berhasil ditolak')
      setShowRejectModal(false)
      setSelectedValidation(null)
      setPassword('')
      setCatatan('')
      fetchValidations()
    } catch (error) {
      showError('Gagal menolak validasi', error.response?.data?.message)
    } finally {
      setProcessing(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID')
  }

  const formatCurrency = (value) => {
    if (!value) return 'Rp 0'
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value)
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Pending':
        return 'badge-warning'
      case 'Pending Penarikan':
        return 'badge-info'
      case 'Approved':
        return 'badge-success'
      case 'Rejected':
        return 'badge-danger'
      default:
        return 'badge-secondary'
    }
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Validasi Anggota Keluar</h3>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-4">
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPage(1)
                }}
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Disetujui</option>
                <option value="Rejected">Ditolak</option>
                <option value="">Semua Status</option>
              </select>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>Tanggal Request</th>
                  <th>NBA</th>
                  <th>Nama</th>
                  <th>Alasan Keluar</th>
                  <th>Request By</th>
                  <th>Simpanan</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Memuat data...
                    </td>
                  </tr>
                ) : !Array.isArray(validations) || validations.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">Tidak ada data</td>
                  </tr>
                ) : (
                  validations.map((validation) => (
                    <tr key={validation.Id}>
                      <td>{formatDate(validation.RequestedAt)}</td>
                      <td>{validation.NBA}</td>
                      <td>{validation.NamaAnggota}</td>
                      <td>{validation.AlasanKeluar}</td>
                      <td>{validation.RequestedBy}</td>
                      <td>
                        <small>
                          Pokok: {formatCurrency(validation.TotalSimpananPokok)}<br/>
                          Wajib: {formatCurrency(validation.TotalSimpananWajib)}<br/>
                          Sukarela: {formatCurrency(validation.TotalSimpananSukarela)}
                        </small>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadge(validation.Status)}`}>
                          {validation.Status}
                        </span>
                      </td>
                      <td>
                        {validation.Status === 'Pending' && (
                          <>
                            <button
                              className="btn btn-success btn-sm mr-1"
                              onClick={() => {
                                setSelectedValidation(validation)
                                setShowApproveModal(true)
                              }}
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                setSelectedValidation(validation)
                                setShowRejectModal(true)
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                        {validation.Status === 'Pending Penarikan' && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setSelectedValidation(validation)
                              handleWithdrawal()
                            }}
                            disabled={processing}
                          >
                            <i className="fas fa-money-bill-wave mr-1"></i>
                            {processing ? 'Memproses...' : 'Tarik Simpanan'}
                          </button>
                        )}
                        {validation.Status === 'Approved' && (
                          <small className="text-success">
                            Selesai<br/>
                            Validated by: {validation.ValidatedBy}
                          </small>
                        )}
                        {validation.Status === 'Rejected' && (
                          <small className="text-danger">
                            Ditolak<br/>
                            Validated by: {validation.ValidatedBy}
                          </small>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Halaman {page} dari {totalPages}
            </div>
            <div>
              <button
                className="btn btn-default btn-sm mr-1"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="btn btn-default btn-sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showApproveModal && selectedValidation && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Setujui Validasi Keluar</h5>
                <button type="button" className="close" onClick={() => setShowApproveModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Anggota akan dikeluarkan dari koperasi. Pastikan simpanan dan pinjaman sudah 0.
                </div>
                <p>Setujui keluar untuk anggota:</p>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>NBA</strong></td>
                      <td>: {selectedValidation.NBA}</td>
                    </tr>
                    <tr>
                      <td><strong>Nama</strong></td>
                      <td>: {selectedValidation.NamaAnggota}</td>
                    </tr>
                    <tr>
                      <td><strong>Alasan Keluar</strong></td>
                      <td>: {selectedValidation.AlasanKeluar}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Pokok</strong></td>
                      <td>: {formatCurrency(selectedValidation.TotalSimpananPokok)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Wajib</strong></td>
                      <td>: {formatCurrency(selectedValidation.TotalSimpananWajib)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Sukarela</strong></td>
                      <td>: {formatCurrency(selectedValidation.TotalSimpananSukarela)}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Masukkan password Anda"
                  />
                </div>
                <div className="form-group">
                  <label>Catatan (opsional)</label>
                  <textarea
                    className="form-control"
                    value={catatan}
                    onChange={(e) => setCatatan(e.target.value)}
                    rows="3"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowApproveModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleApprove}
                  disabled={processing}
                >
                  {processing ? 'Memproses...' : 'Setujui'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && selectedValidation && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Tolak Validasi Keluar</h5>
                <button type="button" className="close" onClick={() => setShowRejectModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>Tolak request keluar untuk anggota:</p>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>NBA</strong></td>
                      <td>: {selectedValidation.NBA}</td>
                    </tr>
                    <tr>
                      <td><strong>Nama</strong></td>
                      <td>: {selectedValidation.NamaAnggota}</td>
                    </tr>
                    <tr>
                      <td><strong>Alasan Keluar</strong></td>
                      <td>: {selectedValidation.AlasanKeluar}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Masukkan password Anda"
                  />
                </div>
                <div className="form-group">
                  <label>Catatan (opsional)</label>
                  <textarea
                    className="form-control"
                    value={catatan}
                    onChange={(e) => setCatatan(e.target.value)}
                    rows="3"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleReject}
                  disabled={processing}
                >
                  {processing ? 'Memproses...' : 'Tolak'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWithdrawModal && withdrawalData && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Bukti Penarikan Simpanan</h5>
                <button type="button" className="close" onClick={() => {
                  setShowWithdrawModal(false)
                  setWithdrawalData(null)
                }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-success">
                  <i className="fas fa-check-circle mr-2"></i>
                  Penarikan simpanan berhasil diproses.
                </div>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>NBA</strong></td>
                      <td>: {withdrawalData.nba}</td>
                    </tr>
                    <tr>
                      <td><strong>Nama</strong></td>
                      <td>: {withdrawalData.nama}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Pokok</strong></td>
                      <td>: {formatCurrency(withdrawalData.simpananPokok)}</td>
                    </tr>
                    <tr>
                      <td><strong>Potongan Penalty Pokok</strong></td>
                      <td>: {formatCurrency(withdrawalData.potonganPokok)}</td>
                    </tr>
                    <tr>
                      <td><strong>Ditarik Pokok</strong></td>
                      <td>: {formatCurrency(withdrawalData.tarikPokok)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Wajib</strong></td>
                      <td>: {formatCurrency(withdrawalData.simpananWajib)}</td>
                    </tr>
                    <tr>
                      <td><strong>Potongan Penalty Wajib</strong></td>
                      <td>: {formatCurrency(withdrawalData.potonganWajib)}</td>
                    </tr>
                    <tr>
                      <td><strong>Ditarik Wajib</strong></td>
                      <td>: {formatCurrency(withdrawalData.tarikWajib)}</td>
                    </tr>
                    <tr className="table-primary">
                      <td><strong>Total Penarikan</strong></td>
                      <td><strong>: {formatCurrency(withdrawalData.totalPenarikan)}</strong></td>
                    </tr>
                    <tr>
                      <td><strong>Penalty</strong></td>
                      <td>: {withdrawalData.penaltyPercent}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowWithdrawModal(false)
                  setWithdrawalData(null)
                }}>
                  Tutup
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePrintReceipt}
                >
                  <i className="fas fa-print mr-2"></i>
                  Cetak Bukti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExitValidations
